import type {CSS} from '@pinecast/styles';
import styled from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import DownChevronMedium from './icons/DownChevronMedium';

const Wrapper = styled(
  'div',
  ({$disabled}: {$disabled: boolean}, designColors) => ({
    alignItems: 'center',
    backgroundColor: designColors.contentBackground,
    borderRadius: 4,
    color: designColors.contentTextAccent,
    display: 'inline-flex',
    fontSize: 14,
    height: 32,
    maxWidth: '100%',
    opacity: 1,
    position: 'relative',
    transition: 'box-shadow 0.2s, opacity 0.2s',
  }),
);
const Select_ = styled(
  'select',
  ({disabled}: {disabled: boolean}, designColors) => ({
    appearance: 'none',
    MozAppearance: 'none',
    WebkitAppearance: 'none',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: designColors.borderGrayNormal,
    boxShadow: `inset 0 0 0 3px transparent, 0 2px 0 1px rgba(0, 0, 0, 0.06), 0 1px 1px rgba(0, 0, 0, 0.12)`,
    borderRadius: 4,
    color: designColors.contentTextAccent,
    flexGrow: 1,
    fontFamily: DEFAULT_FONT,
    fontSize: 14,
    height: 32,
    lineHeight: 32,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    maxWidth: '100%',
    padding: '0 25px 0 10px',
    transition: 'border-color 0.2s, box-shadow 0.2s, opacity 0.2s',
    width: 'auto',

    ':hover': !disabled
      ? {
          // borderColor: designColors.inputBorder,
          boxShadow: `inset 0 0 0 3px ${designColors.borderBlueLight}, 0 2px 0 1px rgba(0, 0, 0, 0.06), 0 1px 1px rgba(0, 0, 0, 0.12)`,
        }
      : undefined,
    ':disabled': {
      opacity: 0.5,
    },

    ':focus': {
      borderColor: designColors.accentBlue,
      boxShadow: `inset 0 0 0 3px ${designColors.accentBlueLight}, 0 2px 0 1px rgba(0, 0, 0, 0.06), 0 1px 1px rgba(0, 0, 0, 0.12)`,
      outline: 'none',
    },
  }),
  {className: 'Select'},
);

export type Option = {
  key: string | number;
  label: string;
};

const Select = ({
  autoFocus,
  disabled,
  nativeEvents,
  onChange,
  options,
  style,
  tabIndex,
  value,
  ...rest
}: {
  'aria-label'?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  nativeEvents?: Partial<{
    onBlur: React.HTMLAttributes<HTMLSelectElement>['onBlur'];
    onChange: React.HTMLAttributes<HTMLSelectElement>['onChange'];
    onClick: React.HTMLAttributes<HTMLSelectElement>['onClick'];
    onFocus: React.HTMLAttributes<HTMLSelectElement>['onFocus'];
    onKeyDown: React.HTMLAttributes<HTMLSelectElement>['onKeyDown'];
    onKeyUp: React.HTMLAttributes<HTMLSelectElement>['onKeyUp'];
    onKeyPress: React.HTMLAttributes<HTMLSelectElement>['onKeyPress'];
  }>;
  onChange: (newValue: string) => void;
  options: Array<Option>;
  style?: CSS;
  tabIndex?: number;
  value: string | number;
}) => (
  <Wrapper style={style} $disabled={disabled || false}>
    <Select_
      autoFocus={autoFocus}
      disabled={disabled || false}
      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
        onChange(e.target.value)
      }
      {...nativeEvents}
      tabIndex={tabIndex}
      value={value}
      {...rest}
    >
      {options.map(({key, label}) => (
        <option key={String(key)} value={key}>
          {label}
        </option>
      ))}
    </Select_>
    <DownChevronMedium
      size={10}
      style={{
        position: 'absolute',
        right: 8,
        top: 0,
        bottom: 0,
        margin: 'auto',
      }}
      aria-hidden="true"
    />
  </Wrapper>
);

export default Select;
